import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listTeamListings, TeamListing } from '../../../../api/stays-team-client';
import { getStaysListing } from '../../../../api/stays-client';
import type { StaysListingHead } from '../../../../interfaces/stays';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { StateSwitch } from '../../../../motion';
import { ListSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { StaysSectionTabs } from '../stays-section';
import { Quiet, StepTitle, Why } from '../setup/setup-wizard.styled';
import { headOf } from '../listing/stays-listing-routes';
import { CheckinTab } from '../checkin/checkin-tab';
import { CardList, CardTitle, Page, PageHead, PropertyCard } from '../properties/properties.styled';

/**
 * GUEST CARDS — /dashboard/stays/guest-cards (Independent Stays IS5, a
 * co-host's own view). One block per listing shared with them: the listing's
 * check-in cards (who is coming, who has checked in, the ID review — IS5-D1),
 * with "Send the link again" as on the listing's own Check-in tab. Amounts
 * never reach a co-host (the server strips them).
 */
export const GuestCardsPage: React.FC = () => {
  const { permissions } = useAuth();
  const canManage = can(permissions, PERMISSIONS.STAYS_MANAGE);
  const [items, setItems] = useState<{ listing: TeamListing; head: StaysListingHead | null }[]>([]);
  const [view, setView] = useState<'loading' | 'error' | 'ready'>('loading');

  const load = useCallback(() => {
    setView('loading');
    listTeamListings()
      .then(async r => {
        const heads = await Promise.all(
          r.items.map(l =>
            getStaysListing(l.listing_id)
              .then(full => headOf(full))
              .catch(() => null)
          )
        );
        setItems(r.items.map((l, i) => ({ listing: l, head: heads[i] })));
        setView('ready');
      })
      .catch(() => setView('error'));
  }, []);
  useEffect(() => load(), [load]);

  return (
    <Page data-guest-cards-page="">
      <StaysSectionTabs />
      <PageHead>
        <div>
          <StepTitle as="h2">Guest cards</StepTitle>
          <Why>Who is arriving at the listings shared with you, and how far each guest is with check-in.</Why>
        </div>
      </PageHead>
      <StateSwitch state={view}>
        {view === 'loading' ? (
          <ListSkeleton rows={3} label="your guest cards" />
        ) : view === 'error' ? (
          <LoadError message="Could not load your guest cards." onRetry={() => load()} />
        ) : items.length === 0 ? (
          <Quiet>No listing is shared with you yet. Ask the owner to share one.</Quiet>
        ) : (
          <CardList aria-label="Your listings">
            {items.map(({ listing, head }) => (
              <PropertyCard key={listing.listing_id} data-guest-cards={listing.listing_id}>
                <CardTitle>
                  <Link to={`/dashboard/stays/${listing.listing_id}`}>{listing.display_name}</Link>
                </CardTitle>
                {head ? <CheckinTab listing={head} canManage={canManage} /> : <Quiet>Could not load this listing.</Quiet>}
              </PropertyCard>
            ))}
          </CardList>
        )}
      </StateSwitch>
    </Page>
  );
};
